import React, { useMemo } from 'react';
import { APITypes, MedicalDataTypes, Dynamo } from '../../../primary-record-types';
import { getIntl } from '../../../utils/intl';
import { FastField } from 'formik';
import { getDateFormatValidator, required } from '../../../utils/validators';
import ImagesField, { formValueImagesToResourceImageUpdates, resourceImagesToFormValueImages } from './ImagesField';
import { FormValueImage } from './ImagesField/types';
import { handleFormikSubmit } from '../../../utils/handleFormikSubmit';
import useRecordEvent from '../../../hooks/useRecordEvent';
import { FormStack } from '../../v2BaseComponents/Stack';
import { CenteredFormRow, Form, SplitFormRow } from '../../v2BaseComponents/Form';
import { FormikInputs } from '../../v2BaseComponents/Inputs';

const intl = getIntl('ImagingsForm', {
    save: 'Save',
    Imaging: 'Imaging',
    notes: 'Additional Information',
    imagingResults: 'Imaging Results',
    dateTime: 'Date and Time',
    ImagingType: 'Imaging Type',
    address1: 'Address 1',
    address2: 'Address 2',
    city: 'City',
    state: 'State',
    zipCode: 'Zip Code',
    selectCareTeamProvider: 'Select Care Team Provider',
    provider: 'Provider',
    providerName: 'Provider Name',
});

export type ImagingFormOwnProps = {
    isLoading: boolean;
    startDateTime?: string;
    defaultResource?: MedicalDataTypes.PrimaryRecordImaging;
    defaultImages?: Dynamo.ResourceImage[];
    onSubmit: (data: APITypes.medicalDataService.UpdateResourceBody<MedicalDataTypes.PrimaryRecordImaging>) => void;
    isDisabled: boolean;
    profileId: string;
    resourceId?: string;
    onCancel: () => void;
};

type ImagingResourceFormValue = MedicalDataTypes.PrimaryRecordImaging;
type FormValue = {
    resource: ImagingResourceFormValue;
    images: FormValueImage[];
};

const getDefaultFormValue = (startDateTime?: string): FormValue => ({
    resource: {
        id: '', //ignored
        version: '1',
        type: 'imaging',
        name: '',
        dateTime: startDateTime || null,
        notes: '',
        address1: '',
        address2: '',
        city: '',
        state: '',
        zipCode: '',
        results: '',
    },
    images: [],
});

export type ImagingFormProps = ImagingFormOwnProps;

const ImagingForm: React.FC<ImagingFormProps> = ({
    isLoading,
    onSubmit,
    isDisabled,
    defaultImages,
    defaultResource,
    startDateTime,
    profileId,
    resourceId,
    onCancel,
}) => {
    const initialValues = useMemo<FormValue>(
        () =>
            defaultResource && defaultImages
                ? {
                      resource: defaultResource,
                      images: resourceImagesToFormValueImages(defaultImages),
                  }
                : getDefaultFormValue(startDateTime),
        [defaultImages, defaultResource, startDateTime],
    );
    const recordEvent = useRecordEvent();

    return (
        <Form
            actionContainerSubmit
            initialValues={initialValues}
            onSubmit={(values, helpers) => {
                onSubmit({
                    resourceUpdate: {
                        ...values.resource,
                    },
                    imageUpdates: formValueImagesToResourceImageUpdates(values.images),
                });
                handleFormikSubmit(values, helpers);
                recordEvent(!!defaultResource ? 'editImaging' : 'createImaging');
            }}
            onCancel={onCancel}
            cancelButtonVariant="secondary"
        >
            {({ values, setFieldValue }) => {
                return (
                    <FormStack>
                        <SplitFormRow>
                            <FastField
                                component={FormikInputs.Input}
                                name="resource.name"
                                isRequired
                                title={intl('Imaging')}
                                isDisabled={isDisabled || isLoading}
                                validate={required}
                            />
                            <FastField
                                name="resource.dateTime"
                                title={intl('dateTime')}
                                component={FormikInputs.DateTimePicker}
                                validate={getDateFormatValidator('MM/DD/YYYY')}
                                isDisabled={isDisabled || isLoading}
                            />
                        </SplitFormRow>
                        <SplitFormRow>
                            <FastField
                                component={FormikInputs.Input}
                                name="resource.results"
                                title={intl('imagingResults')}
                                isDisabled={isDisabled || isLoading}
                            />
                            <FastField
                                component={FormikInputs.TextArea}
                                name="resource.notes"
                                title={intl('notes')}
                                isDisabled={isDisabled || isLoading}
                            />
                        </SplitFormRow>
                        <CenteredFormRow>
                            <ImagesField
                                isDisabled={isDisabled || isLoading}
                                values={values.images}
                                resourceId={resourceId}
                                profileId={profileId}
                                category="imaging"
                                setFieldValue={setFieldValue}
                            />
                        </CenteredFormRow>
                    </FormStack>
                );
            }}
        </Form>
    );
};

export default React.memo(ImagingForm);
